import { Link } from "react-router-dom";
import { Hero } from "@components";
import { header1Styles } from "@consts";

const stats = [
  {
    value: "0.72",
    label: "F1 score",
    detail: "CNN blocking classifier · held-out test",
  },
  {
    value: "78%",
    label: "GTD recall",
    detail: "Expert-validated European summer days",
  },
  {
    value: "3",
    label: "Days all 3 indices agree",
    detail: "Out of 3,680 JJA days · 1979–2018",
  },
];

const stages = [
  {
    title: "Detection",
    status: "Proven",
    tone: "text-accent border-accent/40 bg-accent/10",
    body: "Is a blocking regime present on a given day?",
  },
  {
    title: "Onset warning",
    status: "Validating",
    tone: "text-warn border-warn/40 bg-warn/10",
    body: "Flag a regime forming before standard indices do.",
  },
  {
    title: "Forecast horizon",
    status: "Open question",
    tone: "text-faint border-line/40 bg-elevated/60",
    body: "Can the same signals help days or weeks ahead? Not demonstrated here.",
  },
];

export const Home = () => {
  return (
    <div>
      <Hero />

      <div className="mx-auto max-w-5xl px-4 pb-20">
        {/* Thesis proof points */}
        <section className="mt-6 grid gap-4 sm:grid-cols-3">
          {stats.map((s) => (
            <div key={s.label} className="panel px-5 py-5">
              <p className="font-display text-3xl font-semibold text-accent">{s.value}</p>
              <p className="mt-1 text-sm font-semibold text-ink">{s.label}</p>
              <p className="mt-2 text-xs text-faint">{s.detail}</p>
            </div>
          ))}
        </section>

        {/* Capability overview */}
        <section className="mt-16">
          <p className="section-label">What this shows</p>
          <h2 className={`${header1Styles} mt-2`}>Not another weather app</h2>
          <p className="max-w-2xl text-muted">
            An open look at European atmospheric blocking detection — maps and
            scores from an MSci thesis CNN trained on ERA5, plus recent out-of-sample
            summers.
          </p>

          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {[
              "Automated blocking detection from Z500 fields",
              "Novel ground-truth dataset (3 indices + expert validation)",
              "Onset / decay signals the classic indices miss",
              "Explore maps & timelines for regime awareness",
            ].map((item) => (
              <li
                key={item}
                className="flex gap-3 rounded-xl border border-line/20 bg-surface/40 px-4 py-3 text-sm text-ink"
              >
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-accent" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Capability stages */}
        <section className="mt-16">
          <p className="section-label">Where the science stands</p>
          <h2 className={`${header1Styles} mt-2`}>From detection to open questions</h2>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {stages.map((stage) => (
              <div key={stage.title} className="panel p-5">
                <span
                  className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${stage.tone}`}
                >
                  {stage.status}
                </span>
                <h3 className="mt-3 font-display text-xl font-semibold text-ink">
                  {stage.title}
                </h3>
                <p className="mt-2 text-sm text-muted">{stage.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA strip */}
        <section className="mt-16 overflow-hidden rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/15 via-surface/60 to-navy/20 px-6 py-10 text-center md:px-10">
          <h2 className="font-display text-2xl font-semibold text-ink md:text-3xl">
            Extreme weather doesn&apos;t just happen. Sometimes it gets stuck.
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted">
            Explore daily maps and thesis-era scores — a demonstration of current
            blocking science and ML detection, not a forecast product.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/explore" className="btn-primary">
              Explore
            </Link>
            <Link to="/science" className="btn-ghost">
              Understand the science
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};
